/**
 * Alpine component: quizPlayer
 *
 * Gestisce il ciclo completo del quiz ECM:
 * - Caricamento domande dal proxy AJAX
 * - Una domanda alla volta con navigazione prev/next
 * - Timer countdown (quiz superamento)
 * - Salvataggio risposte in localStorage (resume)
 * - Submit risposte e visualizzazione risultato
 * - Log sessione periodico (keepalive 30s)
 * - Keyboard navigation (frecce, Enter, numeri)
 */
export function registerQuizPlayer(Alpine) {
    Alpine.data('quizPlayer', (config) => ({
        // Config dal template PHP
        subId: config.subId || 0,
        ajaxBase: config.ajaxBase || '',
        quizzes: config.quizzes || [],

        // Stato overlay
        open: false,
        fullscreen: false,
        _scrollY: 0,

        // Stato quiz
        phase: 'idle', // idle | resume | loading | active | submitting | result | error
        currentQuizIndex: -1,
        questions: [],
        currentQuestion: 0,
        answers: {},       // { questionId: [answerId, ...] }
        notes: {},         // { questionId: { answerId: 'text' } }
        timer: 0,          // secondi totali (0 = no timer)
        timeLeft: 0,       // secondi rimanenti
        _timerInterval: null,
        _logInterval: null,
        sessionId: '',
        submitRetries: 0,
        errorMessage: '',

        // Risultato
        result: null,

        // ─────────────────────────────────────────
        // Computed
        // ─────────────────────────────────────────

        get storageKey() {
            if (this.currentQuizIndex < 0) return '';
            const q = this.quizzes[this.currentQuizIndex];
            return q ? 'ik-quiz-' + this.subId + '-' + q.id : '';
        },

        get currentTitle() {
            if (this.currentQuizIndex < 0) return '';
            return this.quizzes[this.currentQuizIndex]?.title || '';
        },

        // Tentativi del test aperto: total 0 = nessun limite, left -1 = illimitato
        get currentAttempts() {
            const q = this.currentQuizIndex >= 0 ? this.quizzes[this.currentQuizIndex] : null;
            return {
                total: q && typeof q.attempts_total === 'number' ? q.attempts_total : 0,
                used: q && typeof q.attempts_used === 'number' ? q.attempts_used : 0,
                left: q && typeof q.attempts_left === 'number' ? q.attempts_left : -1,
            };
        },

        get currentQuizId() {
            if (this.currentQuizIndex < 0) return 0;
            return this.quizzes[this.currentQuizIndex]?.id || 0;
        },

        get currentQuizType() {
            if (this.currentQuizIndex < 0) return 'quiz';
            return this.quizzes[this.currentQuizIndex]?.type || 'quiz';
        },

        get question() {
            return this.questions[this.currentQuestion] || null;
        },

        get isFirst() {
            return this.currentQuestion === 0;
        },

        get isLast() {
            return this.currentQuestion === this.questions.length - 1;
        },

        get answeredCount() {
            let count = 0;
            for (const q of this.questions) {
                if (this.answers[q.id]?.length > 0) count++;
            }
            return count;
        },

        get allAnswered() {
            return this.answeredCount === this.questions.length;
        },

        get submitLabel() {
            const missing = this.questions.length - this.answeredCount;
            if (missing <= 0) return 'Invia risposte';
            return missing === 1 ? 'Manca 1 risposta' : `Mancano ${missing} risposte`;
        },

        get firstUnansweredIndex() {
            return this.questions.findIndex(q => !(this.answers[q.id]?.length > 0));
        },

        get progressPct() {
            if (!this.questions.length) return 0;
            return Math.round(((this.currentQuestion + 1) / this.questions.length) * 100);
        },

        get timerFormatted() {
            const h = Math.floor(this.timeLeft / 3600);
            const m = Math.floor((this.timeLeft % 3600) / 60);
            const s = this.timeLeft % 60;
            const pad = n => String(n).padStart(2, '0');
            return pad(h) + ':' + pad(m) + ':' + pad(s);
        },

        get timerWarning() {
            return this.timer > 0 && this.timeLeft <= 300;
        },

        // ─────────────────────────────────────────
        // Init / Destroy
        // ─────────────────────────────────────────

        init() {},

        destroy() {
            this._stopTimer();
            this._stopLogInterval();
            window.removeEventListener('keydown', this._keyHandler);
        },

        // ─────────────────────────────────────────
        // Overlay
        // ─────────────────────────────────────────

        openQuiz(index) {
            const quiz = this.quizzes[index];
            if (!quiz) return;
            if (quiz.attempts_left === 0 && !quiz.passed) return;

            this.currentQuizIndex = index;
            this.open = true;
            this.questions = [];
            this.currentQuestion = 0;
            this.answers = {};
            this.notes = {};
            this.result = null;
            this.submitRetries = 0;
            this.errorMessage = '';
            this.sessionId = Date.now() + '-' + Math.random().toString(36).slice(2, 9);
            this._scrollY = window.scrollY;
            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.paddingRight = scrollBarWidth + 'px';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this._scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            this._keyHandler = (e) => this._handleKey(e);
            window.addEventListener('keydown', this._keyHandler);

            // Nasconde il widget chat: in overlay si sovrappone ai bottoni del test
            this._hideChat();

            // Check resume
            const saved = this._loadFromStorage();
            this.phase = saved ? 'resume' : 'idle';
        },

        closeQuiz() {
            this._stopTimer();
            this._stopLogInterval();
            window.removeEventListener('keydown', this._keyHandler);
            requestAnimationFrame(() => {
                this.open = false;
                this.fullscreen = false;
                this.phase = 'idle';
                this.currentQuizIndex = -1;
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
                window.scrollTo(0, this._scrollY);

                // Ripristina il widget chat chiuso il test
                this._showChat();

                this._refreshTraining();
            });
        },

        toggleFullscreen() {
            this.fullscreen = !this.fullscreen;
        },

        // ─────────────────────────────────────────
        // Widget chat (SmartSupp via GTM)
        // Nascosto durante il test: sovrasta i bottoni dell'overlay
        // ─────────────────────────────────────────

        _hideChat() {
            if (typeof window.smartsupp === 'function') {
                window.smartsupp('chat:hide');
                return;
            }
            // SmartSupp non ancora caricato (script async / consenso cookie
            // appena dato): riprova finché il test è aperto, max ~9s.
            this._hideChatTries = (this._hideChatTries || 0) + 1;
            if (this._hideChatTries <= 6) {
                setTimeout(() => { if (this.open) this._hideChat(); }, 1500);
            }
        },

        _showChat() {
            this._hideChatTries = 0;
            if (typeof window.smartsupp === 'function') {
                window.smartsupp('chat:show');
            }
        },

        // ─────────────────────────────────────────
        // Quiz lifecycle
        // ─────────────────────────────────────────

        async startQuiz(resume = false) {
            this.phase = 'loading';

            const quizId = this.currentQuizId;
            const type = this.currentQuizType;
            let url = this.ajaxBase + '/quiz.php?sub=' + this.subId
                    + '&quiz=' + quizId
                    + '&action=questions'
                    + '&session=' + encodeURIComponent(this.sessionId);

            if (type === 'survey') url += '&type=survey';

            // Resume: passa ID domande salvate (mantiene ordine, no shuffle)
            const saved = resume ? this._loadFromStorage() : null;
            if (saved?.questionIds?.length) {
                url += '&resume=' + saved.questionIds.join(',');
            }

            try {
                const res = await fetch(url);
                if (!res.ok) {
                    this.errorMessage = this._isAuthError(res.status)
                        ? this._sessionExpiredMessage()
                        : 'Impossibile caricare le domande';
                    this.phase = 'error';
                    return;
                }

                const data = await res.json();
                this.questions = data.questions || [];
                this.timer = data.timer || 0;
                this.timeLeft = this.timer;

                if (!this.questions.length) {
                    this.errorMessage = 'Nessuna domanda disponibile';
                    this.phase = 'error';
                    return;
                }

                // Restore saved state
                if (saved) {
                    this.answers = saved.answers || {};
                    this.notes = saved.notes || {};
                    this.timeLeft = saved.timeLeft ?? this.timer;
                    this.currentQuestion = saved.currentQuestion || 0;
                }

                // Auto-select correct answers (debug/impersonation mode)
                if (!saved) {
                    for (const q of this.questions) {
                        const correct = q.answers.filter(a => a.correct).map(a => a.id);
                        if (correct.length) this.answers[q.id] = correct;
                    }
                }

                this.phase = 'active';

                if (this.timer > 0) this._startTimer();
                this._startLogInterval();

            } catch (e) {
                this.errorMessage = 'Errore di connessione';
                this.phase = 'error';
            }
        },

        async submitQuiz() {
            if (this.phase !== 'active') return;
            this.phase = 'submitting';
            this._stopTimer();
            this._stopLogInterval();

            const quizId = this.currentQuizId;
            const type = this.currentQuizType;
            const questionIds = this.questions.map(q => q.id);

            // Build answers map
            const answersMap = {};
            for (const q of this.questions) {
                const selected = this.answers[q.id] || [];
                answersMap[q.id] = selected.length > 0 ? selected : [0];
            }

            let url = this.ajaxBase + '/quiz.php?sub=' + this.subId
                    + '&quiz=' + quizId + '&action=submit';
            if (type === 'survey') url += '&type=survey';

            try {
                const res = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        started_at: new Date().toISOString(),
                        questions: questionIds,
                        answers: answersMap,
                    }),
                });

                if (!res.ok) {
                    // Survey già completato (422) → tratta come successo
                    if (res.status === 422 && type === 'survey') {
                        this.result = { passed: true };
                        this._clearStorage();
                        this.phase = 'result';
                        return;
                    }
                    // Tentativi esauriti: il server ha rifiutato l'invio, riprovare
                    // non serve e le risposte salvate non servono piu'.
                    if (res.status === 409) {
                        const problem = await res.json().catch(() => ({}));
                        this.errorMessage = problem.detail
                            || 'Hai esaurito i tentativi disponibili per questo test.';
                        this._clearStorage();
                        this._markAttemptsExhausted();
                        this.phase = 'error';
                        return;
                    }
                    // Sessione scaduta/invalidata: riprovare non serve. Le risposte
                    // restano in localStorage: dopo il login si riparte da "Riprendi".
                    if (this._isAuthError(res.status)) {
                        this.errorMessage = this._sessionExpiredMessage();
                        this.phase = 'error';
                        return;
                    }
                    this.submitRetries++;
                    if (this.submitRetries >= 2) {
                        this.errorMessage = 'Invio fallito. Contatta l\'assistenza.';
                        this.phase = 'error';
                    } else {
                        this.errorMessage = 'Errore nell\'invio. Riprova.';
                        this.phase = 'active';
                    }
                    return;
                }

                const data = await res.json();

                // Survey: risposta semplice senza punteggio
                if (type === 'survey') {
                    this.result = { passed: true };
                } else {
                    this.result = {
                        passed: data.passed ?? false,
                        rate: data.rate ?? 0,
                        successRate: data.success_rate ?? 0,
                        totalQuestions: data.total_questions ?? this.questions.length,
                        totalCorrect: data.total_correct_answers ?? 0,
                    };
                }

                this._clearStorage();
                if (type !== 'survey') this._consumeAttempt(this.result?.passed);
                this.phase = 'result';

            } catch (e) {
                this.submitRetries++;
                this.errorMessage = 'Errore di connessione. Riprova.';
                this.phase = 'active';
            }
        },

        _consumeAttempt(passed) {
            const quiz = this.quizzes[this.currentQuizIndex];
            if (!quiz) return;
            if (typeof quiz.attempts_used === 'number') quiz.attempts_used++;
            if (typeof quiz.attempts_left === 'number' && quiz.attempts_left > 0) quiz.attempts_left--;
            if (passed) quiz.passed = true;
        },

        _markAttemptsExhausted() {
            const quiz = this.quizzes[this.currentQuizIndex];
            if (quiz) quiz.attempts_left = 0;
        },

        _isAuthError(status) {
            return status === 401 || status === 403;
        },

        _sessionExpiredMessage() {
            return 'La tua sessione è scaduta. Accedi di nuovo e riapri il test: '
                 + 'le risposte date finora sono salvate su questo dispositivo.';
        },

        // ─────────────────────────────────────────
        // Navigation
        // ─────────────────────────────────────────

        prevQuestion() {
            if (this.currentQuestion > 0) {
                this.currentQuestion--;
                this._saveToStorage();
                this.$nextTick(() => this._onQuestionChange());
            }
        },

        nextQuestion() {
            if (this.isLast && !this.allAnswered) {
                this.goToQuestion(this.firstUnansweredIndex);
                return;
            }
            if (this.currentQuestion < this.questions.length - 1) {
                this.currentQuestion++;
                this._saveToStorage();
                this.$nextTick(() => this._onQuestionChange());
            }
        },

        goToQuestion(index) {
            if (index >= 0 && index < this.questions.length) {
                this.currentQuestion = index;
                this._saveToStorage();
                this.$nextTick(() => this._onQuestionChange());
            }
        },

        _onQuestionChange() {
            this.scrollDotsToActive();
            const body = this.$el.closest('.ik-quiz-body');
            if (body) body.scrollTop = 0;
        },

        scrollDotsToActive() {
            const strip = this.$refs.dotsStrip;
            if (!strip) return;
            const dot = strip.children[this.currentQuestion];
            if (!dot) return;
            const stripRect = strip.getBoundingClientRect();
            const dotRect = dot.getBoundingClientRect();
            const scrollLeft = dot.offsetLeft - (stripRect.width / 2) + (dotRect.width / 2);
            strip.scrollTo({ left: scrollLeft, behavior: 'smooth' });
        },

        // ─────────────────────────────────────────
        // Answers
        // ─────────────────────────────────────────

        toggleAnswer(questionId, answerId) {
            const q = this.questions.find(q => q.id === questionId);
            if (!q) return;

            if (!this.answers[questionId]) {
                this.answers[questionId] = [];
            }

            if (q.type === 'single') {
                this.answers[questionId] = [answerId];
            } else {
                const idx = this.answers[questionId].indexOf(answerId);
                if (idx === -1) {
                    this.answers[questionId].push(answerId);
                } else {
                    this.answers[questionId].splice(idx, 1);
                }
            }

            this._saveToStorage();
        },

        isSelected(questionId, answerId) {
            return (this.answers[questionId] || []).includes(answerId);
        },

        isQuestionAnswered(index) {
            const q = this.questions[index];
            return q ? (this.answers[q.id]?.length > 0) : false;
        },

        setNote(questionId, answerId, text) {
            if (!this.notes[questionId]) this.notes[questionId] = {};
            this.notes[questionId][answerId] = text;
            this._saveToStorage();
        },

        getNote(questionId, answerId) {
            return this.notes[questionId]?.[answerId] || '';
        },

        // ─────────────────────────────────────────
        // Timer
        // ─────────────────────────────────────────

        _startTimer() {
            this._timerInterval = setInterval(() => {
                this.timeLeft--;
                if (this.timeLeft <= 0) {
                    this.timeLeft = 0;
                    this.submitQuiz();
                }
            }, 1000);
        },

        _stopTimer() {
            if (this._timerInterval) {
                clearInterval(this._timerInterval);
                this._timerInterval = null;
            }
        },

        // ─────────────────────────────────────────
        // Session log (keepalive 30s)
        // ─────────────────────────────────────────

        _startLogInterval() {
            this._logInterval = setInterval(() => this._logSession(), 30000);
        },

        _stopLogInterval() {
            if (this._logInterval) {
                clearInterval(this._logInterval);
                this._logInterval = null;
            }
        },

        async _logSession() {
            const type = this.currentQuizType;
            let url = this.ajaxBase + '/quiz.php?sub=' + this.subId
                    + '&quiz=' + this.currentQuizId + '&action=log';
            if (type === 'survey') url += '&type=survey';

            try {
                await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        end: Math.floor(Date.now() / 1000),
                        session: this.sessionId,
                    }),
                });
            } catch (e) { /* silent */ }
        },

        // ─────────────────────────────────────────
        // localStorage (resume)
        // ─────────────────────────────────────────

        _saveToStorage() {
            if (!this.storageKey) return;
            try {
                localStorage.setItem(this.storageKey, JSON.stringify({
                    questionIds: this.questions.map(q => q.id),
                    answers: this.answers,
                    notes: this.notes,
                    timeLeft: this.timeLeft,
                    currentQuestion: this.currentQuestion,
                    timestamp: Date.now(),
                }));
            } catch (e) { /* silent */ }
        },

        _loadFromStorage() {
            if (!this.storageKey) return null;
            try {
                const raw = localStorage.getItem(this.storageKey);
                if (!raw) return null;
                const data = JSON.parse(raw);
                // Scade dopo 30 giorni: i test ECM lunghi (100+ domande) vengono
                // completati in più giornate, anche non consecutive.
                if (Date.now() - data.timestamp > 30 * 86400000) {
                    this._clearStorage();
                    return null;
                }
                return data;
            } catch (e) {
                return null;
            }
        },

        _clearStorage() {
            if (!this.storageKey) return;
            try { localStorage.removeItem(this.storageKey); } catch (e) { /* */ }
        },

        // ─────────────────────────────────────────
        // Keyboard
        // ─────────────────────────────────────────

        _handleKey(e) {
            if (!this.open || this.phase !== 'active') return;

            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    this.prevQuestion();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    this.nextQuestion();
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (this.isLast && this.allAnswered) {
                        this.submitQuiz();
                    } else {
                        this.nextQuestion();
                    }
                    break;
                case '1': case '2': case '3': case '4':
                case '5': case '6': case '7': case '8': case '9':
                    if (this.question) {
                        const idx = parseInt(e.key) - 1;
                        const answer = this.question.answers[idx];
                        if (answer) {
                            this.toggleAnswer(this.question.id, answer.id);
                        }
                    }
                    break;
            }
        },

        // ─────────────────────────────────────────
        // Training refresh (post-quiz)
        // ─────────────────────────────────────────

        async _refreshTraining() {
            try {
                const res = await fetch(this.ajaxBase + '/training.php?sub=' + this.subId);
                if (!res.ok) return;

                const training = await res.json();
                const qzCounter = training.counter?.quizzes || {};
                const svCounter = training.counter?.surveys || {};
                const serverQuizzes = training.quizzes || [];
                const serverSurveys = training.surveys || [];

                // Update local quizzes/surveys passed state
                const qzPassed = this.quizzes.map(q => {
                    if (q.type === 'survey') {
                        const ss = serverSurveys.find(s => s.id === q.id);
                        const passed = ss ? !!ss.completed : q.passed;
                        q.passed = passed;
                        return passed;
                    }
                    const sq = serverQuizzes.find(s => s.id === q.id);
                    const passed = sq ? !!sq.passed : q.passed;
                    q.passed = passed;
                    return passed;
                });

                // Build svPassed from server surveys
                const svPassed = serverSurveys.map(s => !!s.completed);

                window.dispatchEvent(new CustomEvent('quiz-training-updated', {
                    detail: {
                        qzDone: qzCounter.completed ?? 0,
                        qzTotal: qzCounter.total ?? 0,
                        svDone: svCounter.completed ?? 0,
                        svTotal: svCounter.total ?? 0,
                        qzPassed: qzPassed,
                        svPassed: svPassed,
                    },
                }));
            } catch (e) { /* silent */ }
        },
    }));
}
